import { useHydrated, useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { isLaunchLockedPath } from "@/lib/launch-gate";
import { FoundingMembershipCTA } from "@/components/FoundingMembershipCTA";

/**
 * Locked-screen gate for pre-launch member-only routes.
 *
 * Frontend convenience only — every server surface still enforces
 * authorization independently via RLS + `requireSupabaseAuth`. Bypassing
 * this gate on the client gives access to nothing.
 */
export function LaunchLockGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const hydrated = useHydrated();
  const { loading, isAdmin, isMember } = useMembership();

  if (!isLaunchLockedPath(pathname)) return <>{children}</>;

  // While membership is still resolving, show a neutral loading state
  // instead of either flashing the lock (bad for paid members on hard
  // refresh) or the real content (bad for non-members).
  if (!hydrated || loading) {
    return (
      <div className="flex min-h-[60svh] items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--gold-deep)]" aria-hidden="true" />
        <span className="sr-only">Loading your membership…</span>
      </div>
    );
  }

  // Admins and paid members (incl. members post-checkout) bypass.
  if (isAdmin || isMember) return <>{children}</>;

  return <LockedScreen />;
}

function LockedScreen() {
  return (
    <div className="min-h-[100svh] bg-gradient-to-b from-background via-background to-[var(--gold-soft)]/25 px-4 py-16 sm:py-24">
      <div className="mx-auto flex max-w-3xl items-center justify-center">
        <FoundingMembershipCTA ctaHref="/signup" decideLaterHref="/" />
      </div>
    </div>
  );
}
